import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import FlipCard from '../FlipCard/FlipCard';
import ProfileFront from './ProfileFront';
import ProfileBack from './ProfileBack';
import { TooltipContext } from './TooltipContext';
import logo from '../../assets/images/logo.png';
import ShareButton from '../ShareLink/ShareButton';
import './CRNAProfileCard.css';
import { getBackendUrl } from '../../utils/apiUrl';

const BACKEND_URL = getBackendUrl();

const CRNAProfileCard = ({ refreshKey, providerId, profile: providedProfile, readOnly, useProvidedProfile, onFlip, cardHeight, disableFlip, hideShareButton = false, onRequestShare = null, shareTooltip = null, shareVariant = null, skipVerification = false, alignmentData = null, viewerType = 'provider', onBadgeClick, schedule = null, onEditRow = null, hideEmptyRows = false, onRateWithheldClick = null,
  // Builder surfaces put their own flip control somewhere the sticky footer
  // cannot cover, and drive it through this ref instead of FlipCard's own
  // button below the card.
  flipRef = null, hideFlipButton = false,
  // The host shows its own visible flip link (a read-only preview): the card
  // keeps flipping on a tap but is not also a keyboard button.
  externalFlipControl = false,
  // Services use the same profile facts in a compact, unflipped layout.
  // The default card stays unchanged on discovery and profile surfaces.
  presentation = 'card', rateCaption, serviceHeader, children = null, availabilityLabel,
  // The services page card dialog: the service's case types (canonical
  // slugs), highlighted among the proficiencies on the back face.
  highlightCaseTypes = null }) => {
  // Optional-chained on purpose. In useProvidedProfile mode this card fetches
  // nothing, so it has no auth dependency at all, and it is rendered on public
  // marketing surfaces (the /employers hero) and in tests that mount it without
  // an AuthProvider. Destructuring an undefined context threw for both. Read as
  // one expression rather than defaulted to {} so the identity stays stable
  // across renders and the effect below is not re-run on every one.
  const auth = useContext(AuthContext)?.auth;
  const [profile, setProfile] = useState(null);
  const [openId, setOpenId] = useState(null);

  // Set the initial profile if useProvidedProfile is true
  useEffect(() => {
    if (useProvidedProfile && providedProfile) {
      setProfile(providedProfile);
    }
  }, [providedProfile, useProvidedProfile]);

  useEffect(() => {
    // Skip API call if we're using the provided profile
    if (useProvidedProfile) return;

    let cancelled = false;

    (async () => {
      try {
        // Use the provided providerId if available, otherwise use the authenticated user's ID
        const id = providerId || (auth?.user && (auth.user._id || auth.user.id));

        if (!id || !auth?.token) {
          console.warn('Missing provider ID or auth token for profile fetch');
          if (!cancelled) setProfile({});
          return;
        }

        const res = await fetch(
          `${BACKEND_URL}/provider/${id}/profile`,
          { headers: { Authorization: `Bearer ${auth.token}` } }
        );
        const { data } = await res.json();
        // This route returns the RAW profile -- an owner editing their own
        // numbers needs the real figure, so it is never routed through
        // sanitizeProfile (that utility only runs for genuinely public-facing
        // routes: share links, OG cards, recruiter decks). But this same card
        // is also used from the Profile page to show a provider "how
        // employers see you" from their own dashboard, and ProfileFront's
        // privacy gate is keyed on `compensation.isBlind`, a field nothing on
        // this fetch path ever sets. Reproduce sanitizeProfile's one relevant
        // bit here so a private rate previews as private, not as the real
        // number: see ProfileFront.privateRate.test.jsx.
        const withVisibility = data?.rateVisibility === 'private'
          ? { ...data, compensation: { ...data?.compensation, isBlind: true } }
          : data;
        if (!cancelled) setProfile(withVisibility || {});
      } catch (error) {
        console.error('Error fetching provider profile:', error);
        if (!cancelled) setProfile({});
      }
    })();

    return () => { cancelled = true; };
  }, [auth, refreshKey, providerId, useProvidedProfile]);  // ← refetch whenever refreshKey increments or providerId changes

  if (!profile) {
    return (
      <div className="crna-loading" style={cardHeight ? { height: cardHeight } : undefined}>
        <p className="text-brand-muted">Loading profile…</p>
      </div>
    );
  }

  // Enhanced empty state handling
  if (!profile) {
    return (
      <div className="crna-loading" style={cardHeight ? { height: cardHeight } : undefined}>
        <p className="text-brand-muted">Loading profile…</p>
      </div>
    );
  }

  // Check if profile has necessary data to render properly
  const hasMinimalData = schedule || profile.name || profile.email || profile.npi || profile.activeCertifications || profile.availability || profile.yearsOfExperience;
  if (!hasMinimalData) {
    return (
      <div className="crna-profile-card-wrapper" style={cardHeight ? { height: cardHeight } : undefined}>
        <div className="p-4 text-center">
          <h5>Incomplete Profile</h5>
          <p className="text-brand-muted">This profile doesn't have enough information to display</p>
        </div>
      </div>
    );
  }

  // Share button for footer.
  //
  // onRequestShare is the unminted case: a card built in front of the visitor
  // that cannot have a link yet (the anonymous /providers door). It renders the
  // SAME button in the same footer slot, and the tap runs the caller's handler
  // instead of reaching an endpoint no anonymous visitor can use. Without it
  // the button still vanishes on a card with no link, which is what every
  // recruiter-facing deck relies on.
  const shareButtonElement = !hideShareButton ? (
    <ShareButton
      resourceType="crnaProfile"
      resourceId={profile?._id || profile?.id}
      resourceTitle={'Provider Card'}
      variant={shareVariant || 'outline-primary'}
      size="sm"
      autoGenerate={!profile?.shareUrl}
      shareUrl={profile?.shareUrl}
      shareToken={profile?.shareToken}
      onRequestShare={onRequestShare}
      tooltipText={shareTooltip}
      className="shadow-sm bg-white"
    />
  ) : null;

  if (presentation === 'service') {
    return (
      <TooltipContext.Provider value={{ openId, setOpenId }}>
        <div className="crna-service-card" data-component="CRNAProfileCard">
          <ProfileFront p={profile} schedule={schedule} skipVerification
            presentation="service" rateCaption={rateCaption} serviceHeader={serviceHeader} hideEmptyRows />
          {children}
        </div>
      </TooltipContext.Provider>
    );
  }

  return (
    <TooltipContext.Provider value={{ openId, setOpenId }}>
      <FlipCard
        ref={flipRef}
        flippable={!disableFlip}
        isDragging={disableFlip}
        // Builder mode (onEditRow) needs the explicit Flip button even though
        // the preview is readOnly: five of the seven editable rows live on the
        // back face, and tap-the-card-chrome is not a discoverable way there.
        showFlipButton={!hideFlipButton && (!readOnly || Boolean(onEditRow)) && !disableFlip}
        front={<ProfileFront p={profile} rateCaption={rateCaption} skipVerification={skipVerification} alignmentData={alignmentData} viewerType={viewerType} onBadgeClick={onBadgeClick} schedule={schedule} availabilityLabel={availabilityLabel} onEditRow={onEditRow} hideEmptyRows={hideEmptyRows} onRateWithheldClick={onRateWithheldClick} />}
        back={<ProfileBack p={profile} onEditRow={onEditRow} hideEmptyRows={hideEmptyRows} highlightCaseTypes={highlightCaseTypes} />}
        logo={<img src={logo} alt="RxRooster Logo" width={64} height={100} loading="lazy" decoding="async" />}
        shareButton={shareButtonElement}
        onFlip={onFlip}
        keyboardFlip={!externalFlipControl}
      />
    </TooltipContext.Provider>
  );
};

export default CRNAProfileCard;
